(() => {
  if (window.AuremeUrl) return;

  /**
   * path から検索条件と hash を除いた path 部分だけを返す。
   * @param {string} path - 正規化対象の path
   * @returns {string}
   */
  function stripSearchAndHash(path) {
    return String(path).split('#')[0].split('?')[0];
  }

  /**
   * URL オブジェクトを同一オリジン内で使う相対 URL へ変換する。
   * @param {URL | Location} url - 変換対象の URL
   * @returns {string}
   */
  function buildRelativePath(url) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  /**
   * redirect 先として扱える同一オリジン相対 path かどうかを返す。
   * @param {string} path - 判定対象の path
   * @returns {boolean}
   */
  function isSafeRelativePath(path) {
    return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
  }

  /**
   * routes.root_url 由来の末尾 slash を除き、prefix 比較用の形式へ揃える。
   * @param {string} rootUrl - Shopify の root URL
   * @returns {string}
   */
  function normalizeRootUrl(rootUrl) {
    const normalizedRootUrl = String(rootUrl || '').trim();

    return normalizedRootUrl && normalizedRootUrl !== '/'
      ? normalizedRootUrl.replace(/\/+$/, '')
      : '';
  }

  /**
   * locale などの rootUrl prefix を path から取り除く。
   * @param {string} pathname - 正規化対象の pathname
   * @param {string} rootUrl - Shopify の root URL
   * @returns {string}
   */
  function stripRootUrlPrefix(pathname, rootUrl) {
    const normalizedPathname = String(pathname || '');
    const rootUrlPrefix = normalizeRootUrl(rootUrl);
    if (!rootUrlPrefix) return normalizedPathname;
    if (normalizedPathname === rootUrlPrefix) return '/';

    return normalizedPathname.startsWith(`${rootUrlPrefix}/`)
      ? normalizedPathname.slice(rootUrlPrefix.length) || '/'
      : normalizedPathname;
  }

  /**
   * pathname の末尾 slash だけを比較用に正規化する。
   * @param {string} pathname - 正規化対象の pathname
   * @returns {string}
   */
  function normalizePathname(pathname) {
    return String(pathname || '').replace(/\/+$/, '') || '/';
  }

  /**
   * 同一オリジン URL だけを URL オブジェクトとして返す。
   * @param {string} href - 解決対象の href
   * @param {string} [base] - 相対 URL の解決基準
   * @returns {URL | null}
   */
  function getSameOriginUrl(href, base = window.location.href) {
    try {
      const url = new URL(href, base);
      return url.origin === window.location.origin ? url : null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * ログイン復帰先を同一オリジン相対 path へ正規化する。
   * 絶対 URL も pathname/search/hash だけへ畳み込み、外部 redirect として扱わない。
   * @param {string} rawPath - 正規化対象の path
   * @param {string} [fallbackPath] - 不正値の場合の fallback
   * @returns {string}
   */
  function normalizeRedirectPath(rawPath, fallbackPath = window.location.pathname) {
    if (typeof rawPath !== 'string' || rawPath === '') {
      return fallbackPath;
    }

    if (rawPath.startsWith('?') || rawPath.startsWith('#')) {
      return fallbackPath;
    }

    try {
      const resolvedUrl = new URL(rawPath, window.location.origin);
      const redirectPath = buildRelativePath(resolvedUrl);

      return isSafeRelativePath(redirectPath) ? redirectPath : fallbackPath;
    } catch (_error) {
      const queryStrippedPath = stripSearchAndHash(rawPath);

      return isSafeRelativePath(queryStrippedPath) ? queryStrippedPath : fallbackPath;
    }
  }

  /**
   * 検索条件と hash を除いた pathname が一致するかどうかを返す。
   * @param {string} href - 比較対象の URL
   * @param {string} path - 比較対象の path
   * @returns {boolean}
   */
  function isSamePathname(href, path) {
    if (!href || !path) return false;

    try {
      return (
        new URL(href, window.location.origin).pathname ===
        new URL(path, window.location.origin).pathname
      );
    } catch (_error) {
      return stripSearchAndHash(href) === stripSearchAndHash(path);
    }
  }

  window.AuremeUrl = {
    buildRelativePath,
    getSameOriginUrl,
    isSafeRelativePath,
    isSamePathname,
    normalizePathname,
    normalizeRedirectPath,
    normalizeRootUrl,
    stripRootUrlPrefix,
    stripSearchAndHash,
  };
})();
